import { SeatSettings, SettingsContext } from '@distribusion/owl-seats'
import '@distribusion/owl-seats/owl-seats.css'
import React, { FC, PropsWithChildren, useMemo } from 'react'

import config from '@config'
import { usePluginContext } from '@plugins/index'
import usePluginQuery from '@queries/plugin'
import { useSettings } from '@queries/settings'
import { useParams } from '@stores/params'

type SeatsSettingsProps = PropsWithChildren<{ handleLogoClick?: () => void }>

export const SeatsSettingsContext: FC<SeatsSettingsProps> = ({ children, handleLogoClick }) => {
  const [
    {
      endpoints: { newBackend: newBackendEndpoints },
      userInterface: { date, time },
      carrierLogo,
      logo,
      seatSelection: { showFareFeatures, showPrice },
      passengerTypesList,
    },
  ] = useSettings()
  const [{ currency, marketingCarrierCode, retailerPartnerNumber, locale }] = useParams()

  const pluginContext = usePluginContext()
  const pluginQuery = usePluginQuery()

  const pluginData = pluginQuery.data || pluginContext
  const hooks = pluginData?.hooks
  const seatsCustomizations = hooks?.useSeatsCustomizations?.()

  const seatAncillaryAsSelection = marketingCarrierCode
    ? config.seatAncillaryAsSelection?.[marketingCarrierCode]
    : false

  const marketingCarrierCodes = useMemo(() => {
    const retailerCarriers = config.retailerDiscounts[retailerPartnerNumber]?.discounts ?? []
    return [marketingCarrierCode, ...retailerCarriers].filter<string>((value): value is string => value != null)
  }, [marketingCarrierCode, retailerPartnerNumber])
  const value = useMemo<SeatSettings>(
    () => ({
      locale: locale,
      maxPassengers: passengerTypesList.totalPaxLimit,
      currency,
      topCurrencies: config.settings.topCurrencies,
      seatAncillaryAsSelection,
      retailerPartnerNumber,
      discounts: { marketingCarrierCodes },
      newBackendEndpoints,
      api: config.api,
      userInterface: { date, time },
      carrierLogo,
      showFareFeatures,
      showPrice,
      logo: {
        url: logo.url || undefined,
        redirectionUrl: logo.redirectionUrl || undefined,
        handleClick: handleLogoClick,
      },
      checkIsFemaleOnly: seatsCustomizations?.isFemaleOnly,
    }),
    [
      seatsCustomizations,
      carrierLogo,
      currency,
      date,
      handleLogoClick,
      locale,
      passengerTypesList.totalPaxLimit,
      logo.redirectionUrl,
      logo.url,
      marketingCarrierCodes,
      newBackendEndpoints,
      retailerPartnerNumber,
      seatAncillaryAsSelection,
      showFareFeatures,
      showPrice,
      time,
    ],
  )

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>
}
